// Utilidades puras da tela de Entregas mensais (formatação e "colar da planilha").
// O cálculo de verdade é do servidor (entregasMensaisRegras.js); aqui só se lê.

export const SETOR_NOMES: Record<string, string> = {
  projetos: 'Projetos Civis e Saneamento',
  topografia_hidrometria: 'Topografia e Hidrometria',
  hidrologia_meio_ambiente: 'Hidrologia e Meio Ambiente',
  fiscalizacao: 'Fiscalização de Projetos',
  seguranca_barragens: 'Segurança de Barragens',
  sem_setor: 'Sem setor',
};
export const SETOR_CURTO: Record<string, string> = {
  projetos: 'Projetos', topografia_hidrometria: 'Topo/Hidrometria', hidrologia_meio_ambiente: 'Hidrologia/MA',
  fiscalizacao: 'Fiscalização', seguranca_barragens: 'Barragens', sem_setor: 'Sem setor',
};
// Paleta categórica fixa por setor (a mesma em todos os gráficos; ordem fixa,
// passos validados para daltonismo na paleta de referência de gráficos).
export const SETOR_COR: Record<string, string> = {
  projetos: '#2a78d6', topografia_hidrometria: '#eb6834', hidrologia_meio_ambiente: '#1baf7a',
  fiscalizacao: '#eda100', seguranca_barragens: '#e87ba4', sem_setor: '#898781',
};
// Séries do painel: baseline (azul claro), ajuste (azul), realizado (verde-água); meta em tinta.
export const SERIE_COR = { baseline: '#86b6ef', ajuste: '#2a78d6', realizado: '#1baf7a', meta: '#52514e' };
export const ORDEM_SETORES = ['projetos', 'topografia_hidrometria', 'hidrologia_meio_ambiente', 'fiscalizacao', 'seguranca_barragens', 'sem_setor'];
export const PROBABILIDADES = [
  { value: 90, label: '90% — alta' }, { value: 50, label: '50% — média' }, { value: 10, label: '10% — baixa' },
  { value: 100, label: '100% — certa' }, { value: 0, label: '0% — fora do mês' },
];
export const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
export const MESES_CURTOS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

export const brl = (v: number | null | undefined, casas = 0): string =>
  v == null ? '—' : 'R$ ' + Number(v).toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas });
export const brlCurto = (v: number | null | undefined): string => {
  if (v == null) return '—';
  const n = Number(v);
  if (Math.abs(n) >= 1e6) return 'R$ ' + (n / 1e6).toLocaleString('pt-BR', { maximumFractionDigits: 2 }) + ' mi';
  if (Math.abs(n) >= 1e3) return 'R$ ' + (n / 1e3).toLocaleString('pt-BR', { maximumFractionDigits: 0 }) + ' mil';
  return brl(n);
};
export const pct = (v: number | null | undefined, casas = 1): string =>
  v == null ? '—' : `${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas })}%`;
export const dataBr = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const [a, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}/${m}/${a}`;
};
export const nomeDe = (email: string | null | undefined): string => {
  if (!email) return '—';
  const base = String(email).split('@')[0].split(/[._]/)[0];
  return base.charAt(0).toUpperCase() + base.slice(1);
};
export const rotuloCompetencia = (comp: string): string => {
  const [a, m] = comp.split('-').map(Number);
  return `${MESES[m - 1]}/${a}`;
};
/** Rateio {setor: 0..1} → "Topo/Hidrometria 60% · Projetos 40%". */
export const textoRateio = (r: Record<string, number> | null | undefined): string =>
  Object.entries(r || {}).sort((a, b) => b[1] - a[1])
    .map(([k, p]) => `${SETOR_CURTO[k] || k}${Object.keys(r || {}).length > 1 ? ' ' + Math.round(p * 100) + '%' : ''}`).join(' · ');

export function moeda(v: string | number | null | undefined): number | null {
  if (v == null) return null;
  if (typeof v === 'number') return v;
  const s = v.replace(/R\$\s*/g, '').replace(/\s/g, '').trim();
  if (!s || s === '-') return null;
  const neg = s.startsWith('-');
  const n = parseFloat(s.replace(/^-/, '').replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? (neg ? -n : n) : null;
}

/** Equipe da planilha → chave de setor (mesma régua do servidor). */
export function setorDoRotulo(rotulo: string): string | null {
  const t = String(rotulo || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
  if (!t) return null;
  if (t.includes('barrag')) return 'seguranca_barragens';
  if (t.includes('fiscaliz') || t.includes('supervis')) return 'fiscalizacao';
  if (t.includes('topograf') || t.includes('hidrometr')) return 'topografia_hidrometria';
  if (t.includes('hidrolog') || t.includes('ambient')) return 'hidrologia_meio_ambiente';
  if (t.includes('projet') || t.includes('saneamento')) return 'projetos';
  return null;
}

/**
 * Linhas copiadas de uma aba mensal (colunas B em diante: Projeto, Valor total,
 * Entregue acumulado, Saldo, …, Equipe). A equipe é achada pela coluna que
 * parece setor; linha sem nome ou sem valor total é ignorada.
 */
export function lerLinhasAbaMensal(texto: string) {
  const out: { rotulo: string; valor_total: number; entregue_acumulado: number; saldo: number; equipe: string }[] = [];
  for (const bruta of String(texto || '').split(/\r?\n/)) {
    const cols = bruta.split('\t').map(c => c.trim());
    while (cols.length && cols[0] === '') cols.shift();
    if (cols.length < 3) continue;
    const rotulo = cols[0];
    const total = moeda(cols[1]);
    if (!rotulo || total == null || /^projeto$/i.test(rotulo)) continue;
    const equipe = cols.slice(4).find(c => setorDoRotulo(c)) || '';
    out.push({ rotulo, valor_total: total, entregue_acumulado: moeda(cols[2]) ?? 0, saldo: moeda(cols[3]) ?? 0, equipe });
  }
  return out;
}

/**
 * Linhas copiadas do Resumo (colunas B:P): mês | baseline | ajustado | meta |
 * entregas total | projetos | % | topo | % | hidro | % | fiscalização | % | barragens | %.
 * Trimestres e totais (sem mês) são ignorados.
 */
export function lerResumo(texto: string, ano: number) {
  const setores = ['projetos', 'topografia_hidrometria', 'hidrologia_meio_ambiente', 'fiscalizacao', 'seguranca_barragens'];
  const out: { competencia: string; tipo: string; setor: string; valor: number | null }[] = [];
  for (const bruta of String(texto || '').split(/\r?\n/)) {
    const cols = bruta.split('\t').map(c => c.trim());
    while (cols.length && cols[0] === '') cols.shift();
    const m = (cols[0] || '').toLowerCase().match(/^([a-zç]+)\s*\/\s*(\d{4})$/);
    if (!m) continue;
    const idx = MESES.indexOf(m[1]);
    if (idx < 0 || Number(m[2]) !== ano) continue;
    const comp = `${ano}-${String(idx + 1).padStart(2, '0')}-01`;
    out.push({ competencia: comp, tipo: 'baseline', setor: 'total', valor: moeda(cols[1]) });
    out.push({ competencia: comp, tipo: 'ajuste', setor: 'total', valor: moeda(cols[2]) });
    out.push({ competencia: comp, tipo: 'realizado', setor: 'total', valor: moeda(cols[4]) });
    setores.forEach((s, i) => out.push({ competencia: comp, tipo: 'realizado', setor: s, valor: moeda(cols[5 + i * 2]) }));
  }
  return out;
}
